/**
 * The export menu — Resource Plan Epic 10 (RP-10.1, RP-10.2), M4.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THE VARIANT LABELS SAY WHAT IS IN THE FILE, NOT WHAT THE FILE IS CALLED
 * ──────────────────────────────────────────────────────────────────────────────
 * "Client" and "Internal" are not self-explanatory to someone who is about to email one of
 * them to a client, so each option states its own contents — and the internal one states
 * what it must not be sent to. §14 row 6 makes cost commercially sensitive, and an export
 * is the easiest way for it to leave the building; a label is cheaper than an incident.
 *
 * The Internal option is offered only when `plan.canViewCost`. It is enforced server-side
 * on `viewPlanCost` regardless (see src/api/plan-export.ts) — this is so an `[ACCT]`
 * planner is not shown a button that 403s.
 */

import { FileSpreadsheet, FileText } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useExportPlan, usePlanExportOptions } from "@/hooks/use-plan-governance";
import type { PlanExportVariant } from "@/lib/plan-export/model";
import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

const VARIANTS: Array<{ id: PlanExportVariant; label: string; blurb: string; cost: boolean }> = [
  {
    id: "client",
    label: "Client",
    blurb:
      "Workstreams, Labor Categories, monthly hours and totals. On a T&M plan it carries the applied rates and revenue; on a Fixed Price plan, the Contracted Fee only. No cost, no margin, no list rates.",
    cost: false,
  },
  {
    id: "clientRatesFree",
    label: "Client, rates-free",
    blurb: "The same, with rates and revenue removed entirely. Hours and structure only.",
    cost: false,
  },
  {
    id: "internal",
    label: "Internal",
    blurb: "Adds cost, margin, list-vs-applied rates and the approval history. NOT for the client.",
    cost: true,
  },
];

/**
 * The export options, anchored under the plan header's ⋯ menu (its trigger lives there).
 * The parent wraps the ⋯ button and this panel in one `relative` box.
 */
export function PlanExportPanel({
  plan,
  open,
  onOpenChange,
}: {
  plan: ResourcePlanDetailView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const setOpen = onOpenChange;
  const { data: options } = usePlanExportOptions(plan.id, open);
  const exportPlan = useExportPlan(plan.id);

  const [variant, setVariant] = useState<PlanExportVariant>("client");
  const [versionId, setVersionId] = useState("");

  const available = VARIANTS.filter((v) => !v.cost || plan.canViewCost);
  const chosen = available.find((v) => v.id === variant) ?? available[0];

  return (
    <>
      {open && (
        <>
          {/* Click-away. A plain overlay rather than a Radix popover: the menu is a leaf
              with no focus trap worth managing, and the plan header already carries three
              dialogs. */}
          <button
            type="button"
            aria-label="Close the export menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          {/*
            `t-dropdown is-open`, not `t-dropdown` alone. The base class in
            styles/globals.css is the CLOSED state — opacity 0, pointer-events none — and
            `is-open` (or a Radix `data-state="open"`) is what reveals it. A dropdown with
            only the base class renders fully laid out and completely invisible, which is
            the least debuggable failure the motion system can produce.
          */}
          <div
            data-origin="top-right"
            className="t-dropdown is-open absolute right-0 z-50 mt-1.5 w-[380px] space-y-3 rounded-lg border border-border bg-popover p-3 shadow-lg"
          >
            <div className="space-y-1.5">
              {available.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setVariant(option.id)}
                  className={cn(
                    "block w-full rounded-md border px-2.5 py-2 text-left transition-colors",
                    variant === option.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50",
                  )}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    {option.label}
                    {option.cost && (
                      <span className="rounded bg-error-100 px-1 py-0.5 text-xs font-semibold uppercase tracking-wider text-error-700 dark:text-error-400">
                        contains cost
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
                    {option.blurb}
                  </span>
                </button>
              ))}
            </div>

            {options !== undefined && options.versions.length > 0 && (
              <div className="space-y-1">
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  What to export
                </span>
                <select
                  value={versionId}
                  onChange={(e) => setVersionId(e.target.value)}
                  className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
                  aria-label="Which version to export"
                >
                  <option value="">The plan as it stands now</option>
                  {options.versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  A frozen version exports what was actually sent — it cannot have repriced since.
                </p>
              </div>
            )}

            <div className="flex items-center gap-2 border-t border-border pt-2.5">
              <Button
                size="sm"
                className="flex-1"
                disabled={exportPlan.isPending}
                onClick={() =>
                  exportPlan.mutate(
                    {
                      variant: chosen?.id ?? "client",
                      format: "xlsx",
                      versionId: versionId === "" ? null : versionId,
                    },
                    { onSuccess: () => setOpen(false) },
                  )
                }
              >
                <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
                Excel
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1"
                disabled={exportPlan.isPending}
                onClick={() =>
                  exportPlan.mutate(
                    {
                      variant: chosen?.id ?? "client",
                      format: "pdf",
                      versionId: versionId === "" ? null : versionId,
                    },
                    { onSuccess: () => setOpen(false) },
                  )
                }
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" />
                PDF
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
